/**
 * Tap and hold, without the bug that made a slow scroll open a menu.
 *
 * The wish tile was the last place in the app still running the old version: a
 * 500ms timer cancelled only by onTouchMove. Once the browser takes a gesture
 * over for scrolling it stops delivering touchmove to the element, so a thumb
 * resting on a card while the grid slid past never cancelled anything and the
 * menu opened under a finger that was already moving. Distance was the wrong
 * question - a scroll that has begun is categorically not a press, at any speed
 * and over any distance.
 *
 * So the hold is cancelled by the scroll event itself, listened for in the
 * capture phase on document because scroll does not bubble and several
 * different elements scroll in this app. The drift check stays only for the
 * case no scroll event can fire: a drag across a list already pinned at its top
 * or bottom.
 *
 * This is the same mechanism as Collection.js's cancelOnScroll + longPressProps,
 * lifted out so the wish surfaces share it rather than copy it.
 */

const HOLD_MS = 420;
const HOLD_DRIFT_PX = 12;

/**
 * Call `fn` the first time anything scrolls, then stop listening.
 * @returns {Function} stop listening without calling `fn`
 */
export function cancelOnScroll(fn) {
  if (typeof document === 'undefined') return () => {};
  let done = false;
  const handler = () => {
    if (done) return;
    done = true;
    document.removeEventListener('scroll', handler, { capture: true });
    fn();
  };
  document.addEventListener('scroll', handler, { capture: true, passive: true });
  return () => {
    if (done) return;
    done = true;
    document.removeEventListener('scroll', handler, { capture: true });
  };
}

/**
 * Touch handlers for a tile that opens a menu on hold and does something else
 * on tap.
 *
 * `firedRef` is shared with the tap handler so the tap iOS sends at the end of
 * a hold is swallowed rather than opening the card detail behind the menu.
 *
 * @param {Object} options
 * @param {Function} options.onHold   called once, when the hold is real
 * @param {Object}   options.firedRef a ref the caller also reads in its onClick
 * @param {boolean}  options.disabled someone else's list holds nothing
 */
export function holdProps({ onHold, firedRef, disabled = false }) {
  if (disabled) return {};
  let timer = null;
  let stopScrollWatch = null;
  let start = null;

  const clear = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (stopScrollWatch) { stopScrollWatch(); stopScrollWatch = null; }
    start = null;
  };

  const fire = () => {
    timer = null;
    if (firedRef) firedRef.current = true;
    onHold();
    // The flag must not lie in wait for the next genuine tap if no tap follows
    // the hold, which is what happens when the finger drifts off the tile.
    if (firedRef) setTimeout(() => { firedRef.current = false; }, 500);
  };

  return {
    onTouchStart: (e) => {
      clear();
      const t = e.touches?.[0];
      start = t ? { x: t.clientX, y: t.clientY } : null;
      timer = setTimeout(fire, HOLD_MS);
      stopScrollWatch = cancelOnScroll(clear);
    },
    onTouchEnd: clear,
    onTouchCancel: clear,
    onTouchMove: (e) => {
      if (!start) return;
      const t = e.touches?.[0];
      if (!t) return;
      if (Math.hypot(t.clientX - start.x, t.clientY - start.y) > HOLD_DRIFT_PX) clear();
    },
    onContextMenu: (e) => { e.preventDefault(); },
  };
}

export { HOLD_MS, HOLD_DRIFT_PX };
